import {
  TotalCountResponse,
  CustomerList,
  ProgressSearchResponse,
  StudentDetail,
  StudentList,
  Yomi3ProbabilityCalcResult,
} from '@agent-ds/shared/interfaces';
import {
  MonthlySalesOverview,
  SalesAmount,
  SalesAmountForUser,
  TeamMonthlySalesOverviews,
} from '@agent-ds/shared/interfaces/sale';
import { ApiService } from '@agent-ds/shared/models/api-service';
import { toAsialRequestParameters } from '@agent-ds/shared/util/util';
import { StudentSearchRequest } from '@agent-ds/student/dto/studentSearchRequest';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class CustomerApiService extends ApiService {
  constructor(private http: HttpClient) {
    super();
  }

  getList(search: StudentSearchRequest): Observable<StudentList> {
    return this.http.get<StudentList>('/api/customer/search', { params: search as any });
  }

  getTeamCustomerList(teamId: number, params: any): Observable<CustomerList> {
    return this.http.get<CustomerList>(`/api/customer/searchTeamCustomer/${teamId}`, { params: params as any });
  }

  getMySalesTargetCustomerListOfExpectedContractMonth(targetDateString: string, search: StudentSearchRequest): Observable<StudentList> {
    const startOfTargetMonthDateFormattedString = moment(targetDateString)
        .startOf('month') // 月初に設定
        .format('YYYY-MM-DD');
    const endOfTargetMonthDateFormattedString = moment(targetDateString)
        .endOf('month') // 月末に設定
        .format('YYYY-MM-DD');
    const searchQuery = {
      ...search,
      expectedContractOrDeclinedDateFrom: startOfTargetMonthDateFormattedString,
      expectedContractOrDeclinedDateTo: endOfTargetMonthDateFormattedString,
    };
    return this.http.get<StudentList>('/api/customer/searchMySalesTarget', { params: searchQuery as any });
  }

  getTeamSalesTargetCustomerListOfExpectedContractMonth(teamId: number, targetDateString: string, search: StudentSearchRequest): Observable<StudentList> {
    const startOfTargetMonthDateFormattedString = moment(targetDateString)
        .startOf('month') // 月初に設定
        .format('YYYY-MM-DD');
    const endOfTargetMonthDateFormattedString = moment(targetDateString)
        .endOf('month') // 月末に設定
        .format('YYYY-MM-DD');
    const searchQuery = {
      ...search,
      expectedContractOrDeclinedDateFrom: startOfTargetMonthDateFormattedString,
      expectedContractOrDeclinedDateTo: endOfTargetMonthDateFormattedString,
    };

    return this.http.get<StudentList>(`/api/customer/searchTeamSalesTarget/${teamId}`, { params: searchQuery as any });
  }

  getMyRaSalesTargetCustomerListOfExpectedContractMonth(targetDateString: string, search: StudentSearchRequest): Observable<ProgressSearchResponse> {
    const startOfTargetMonthDateFormattedString = moment(targetDateString)
        .startOf('month') // 月初に設定
        .format('YYYY-MM-DD');
    const endOfTargetMonthDateFormattedString = moment(targetDateString)
        .endOf('month') // 月末に設定
        .format('YYYY-MM-DD');
    const searchQuery = {
      ...search,
      expectedContractOrDeclinedDateFrom: startOfTargetMonthDateFormattedString,
      expectedContractOrDeclinedDateTo: endOfTargetMonthDateFormattedString,
    };
    return this.http.get<ProgressSearchResponse>('/api/customer/searchMyRaSalesTarget', { params: searchQuery as any });
  }

  getMyRaSalesTargetCustomerTotalOfExpectedContractMonth(targetDateString: string, search: StudentSearchRequest): Observable<TotalCountResponse> {
    const startOfTargetMonthDateFormattedString = moment(targetDateString)
        .startOf('month')
        .format('YYYY-MM-DD');
    const endOfTargetMonthDateFormattedString = moment(targetDateString)
        .endOf('month')
        .format('YYYY-MM-DD');
    const searchQuery = {
      ...search,
      expectedContractOrDeclinedDateFrom: startOfTargetMonthDateFormattedString,
      expectedContractOrDeclinedDateTo: endOfTargetMonthDateFormattedString,
    };
    return this.http.get<TotalCountResponse>('/api/customer/searchMyRaSalesTargetTotal', { params: searchQuery as any });
  }

  getTeamRaSalesTargetCustomerListOfExpectedContractMonth(teamId: number, targetDateString: string, search: StudentSearchRequest): Observable<ProgressSearchResponse> {
    const startOfTargetMonthDateFormattedString = moment(targetDateString)
        .startOf('month') // 月初に設定
        .format('YYYY-MM-DD');
    const endOfTargetMonthDateFormattedString = moment(targetDateString)
        .endOf('month') // 月末に設定
        .format('YYYY-MM-DD');
    const searchQuery = {
      ...search,
      expectedContractOrDeclinedDateFrom: startOfTargetMonthDateFormattedString,
      expectedContractOrDeclinedDateTo: endOfTargetMonthDateFormattedString,
    };

    return this.http.get<ProgressSearchResponse>(`/api/customer/searchTeamRaSalesTarget/${teamId}`, { params: searchQuery as any });
  }

  getTeamRaSalesTargetCustomerTotalOfExpectedContractMonth(teamId: number, targetDateString: string, search: StudentSearchRequest): Observable<TotalCountResponse> {
    const startOfTargetMonthDateFormattedString = moment(targetDateString)
        .startOf('month')
        .format('YYYY-MM-DD');
    const endOfTargetMonthDateFormattedString = moment(targetDateString)
        .endOf('month')
        .format('YYYY-MM-DD');
    const searchQuery = {
      ...search,
      expectedContractOrDeclinedDateFrom: startOfTargetMonthDateFormattedString,
      expectedContractOrDeclinedDateTo: endOfTargetMonthDateFormattedString,
    };

    return this.http.get<TotalCountResponse>(`/api/customer/searchTeamRaSalesTargetTotal/${teamId}`, { params: searchQuery as any });
  }

  getDetail(id: number): Observable<StudentDetail> {
    return this.http.get<StudentDetail>('/api/customers/' + id).pipe(
      tap((student) => {
        student.userId = student.studentUsers && student.studentUsers.length ? student.studentUsers[0].userId : null;
        // 副担当者のIDを抽出
        if (student.studentSubUsers && student.studentSubUsers.length) {
          student.subUserIds = student.studentSubUsers.map((studentSubUser) => {
            return studentSubUser.userId;
          });
        } else {
          // do nothing
        }
        return student;
      }),
    );
  }

  calcYomi3Probability(search: { yomi3Passed: string; yomi3Inclination: string; yomi3Schedule: string; }): Observable<Yomi3ProbabilityCalcResult> {
    return this.http.get<Yomi3ProbabilityCalcResult>(`/api/customer/progress/calcYomi3Probability`, { params: search as any });
  }

  /*
  後々拡張の可能性あり
  updateMemo(id: number, memo: SituationMemo): Observable<any> {
    return this.http.put(`/api/customers/${id}/memos/${memo.id}`, memo);
  }

  updateNote(studentId: number, note: StudentNote): Observable<any> {
    return this.http.put(`/api/customers/${studentId}/note/${note.id}`, note);
  }
  */

  updateExpectedSK(studentId: number, progressId: number, expectedSk: number): Observable<any> {
    return this.http.put(
      `/api/customer/progress/expectedSk`,
      {studentId, progressId, expectedSk}
    );
  }

  updateExpectedCompanyFlag(studentId: number, currentProgressId: number, updateProgressId: number): Observable<any> {
    return this.http.put(
      `/api/customer/expected/${studentId}/${currentProgressId}/${updateProgressId}`,
      {}
    );
  }

  updateExpectedCompany2Flag(studentId: number, currentProgressId: number, updateProgressId: number): Observable<any> {
    return this.http.put(
      `/api/customer/expectedCompany2/${studentId}/${currentProgressId}/${updateProgressId}`,
      {}
    );
  }

  updateExpectedContractMonth(studentId: number, expectedContractMonth: string | null): Observable<any> {
    return this.http.put(
      `/api/customer/expectedContractMonth/${studentId}`,
      { expectedContractMonth }
    );
  }
  updateExpectedContractWeek(studentId: number, expectedContractWeek: string): Observable<any> {
    return this.http.put(
      `/api/customer/expectedContractWeek/${studentId}`,
      { expectedContractWeek }
    );
  }

  updateRaExpectedContractMonth(progressId: number, expectedContractMonth: string | null): Observable<any> {
    return this.http.put(
      `/api/customer/progress/${progressId}/raExpectedContractMonth`,
      { progressId, expectedContractMonth }
    );
  }
  updateRaExpectedContractWeek(progressId: number, expectedContractWeek: string): Observable<any> {
    return this.http.put(
      `/api/customer/progress/${progressId}/raExpectedContractWeek`,
      { progressId, expectedContractWeek }
    );
  }

  updateYomi(id: number, request: { yomi: string }): Observable<any> {
    return this.http.put(`/api/customer/${id}/yomi`, request);
  }
  updateRaYomi(progressId: number, request: { yomi: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${progressId}/raYomi`, request);
  }
  updateYomi3Passed(request: { studentId: number, progressId: number, yomi3Value: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/yomi3Passed`, request);
  }
  updateYomi3Inclination(request: { studentId: number, progressId: number, yomi3Value: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/yomi3Inclination`, request);
  }
  updateYomi3Schedule(request: { studentId: number, progressId: number, yomi3Value: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/yomi3Schedule`, request);
  }
  updateRaYomi3Passed(request: { studentId: number, progressId: number, yomi3Value: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/raYomi3Passed`, request);
  }
  updateRaYomi3Inclination(request: { studentId: number, progressId: number, yomi3Value: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/raYomi3Inclination`, request);
  }
  updateRaYomi3Schedule(request: { studentId: number, progressId: number, yomi3Value: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/raYomi3Schedule`, request);
  }
  updateRaMemo(request: { progressId: number, memoString: string }): Observable<any> {
    return this.http.put(`/api/customer/progress/${request.progressId}/raMemo`, request);
  }

  getSalesReportOfCurrentUser(query: any): Observable<SalesAmountForUser> {
    const params = query ? toAsialRequestParameters(query) : {};
    return this.http.get<SalesAmountForUser>(`/api/customer/sales/amountOfCurrentUser`, { params: params as any });
  }
  getSalesReport(query: any): Observable<SalesAmount> {
    const params = query ? toAsialRequestParameters(query) : undefined;
    return this.http.get<SalesAmount>(`/api/customer/sales/amount`, { params: (params ? params : {}) as any });
  }

  getTeamMonthlySalesOverviews(teamId: number, year: string, month: string, query: any): Observable<TeamMonthlySalesOverviews> {
    const params: any = query ? toAsialRequestParameters(query) : {};
    params.teamId = [teamId];
    return this.http.get<TeamMonthlySalesOverviews>(`/api/customer/sales/monthlySalesOverview/${year}/${month}`, { params: params as any});
  }

  getMyMonthlySalesOverview(year: string, month: string, query: any): Observable<MonthlySalesOverview> {
    const params = query ? toAsialRequestParameters(query) : {};
    return this.http.get<MonthlySalesOverview>(`/api/customer/sales/monthlySalesOverviewOfCurrentUser/${year}/${month}`, { params: params as any });
  }

  updateMyMonthlySalesOverview(year: string, month: string, input: {targetGk?: number, targetSn?: number, externalRevenue?: number}): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(
      `/api/customer/sales/monthlySalesOverviewOfCurrentUser/${year}/${month}`,
      input
    );
  }

}
